import { getCookie } from "../../hooks/getCookie";
import { setCookie } from "../../hooks/setCookie";

const VISITOR_COOKIE = "visitor_id";
const SESSION_COOKIE = "session_id";
const USER_COOKIE = "user_id";

const VISITOR_MAX_AGE_DAYS = 365;
const SESSION_MAX_AGE_HOURS = 2;
const SESSION_MAX_AGE_MS = SESSION_MAX_AGE_HOURS * 60 * 60 * 1000;

const uuid = (): string => {
  const cryptoObj: Crypto | undefined =
    typeof window !== "undefined" ? window.crypto : undefined;
  if (cryptoObj && typeof (cryptoObj as Crypto).randomUUID === "function") {
    return (cryptoObj as Crypto).randomUUID();
  }
  // RFC4122 v4 fallback
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

// Las tres cookies de identidad son analítica propia (no medición anónima de
// atribución): `setCookie` las difiere hasta que CookieYes acepte "analytics"
// (ver consentGatedCookies) y `getCookie` devuelve mientras tanto el valor
// retenido en sesión, así que los ids se mantienen estables dentro de la
// sesión aunque aún no haya cookie.
const writeCookie = (name: string, value: string, expires: Date): void =>
  setCookie(name, value, expires);

const daysFromNow = (days: number): Date => {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d;
};

const hoursFromNow = (hours: number): Date => {
  const d = new Date();
  d.setHours(d.getHours() + hours);
  return d;
};

const readCookie = (name: string): string | null => {
  const v = getCookie(name);
  if (!v || v === "defined") return null;
  return v as string;
};

/**
 * Lo que sabe esta carga de página: qué ids ha creado ella (y no venían de la
 * cookie ni de la cola del consentimiento) y, por si el navegador no deja
 * guardar nada (sin cookies ni sessionStorage), los ids en memoria para que no
 * cambien en cada evento.
 */
interface PageIdentity {
  createdSessions: Record<string, true>;
  createdVisitors: Record<string, true>;
  visitor: string | null;
  session: { id: string; expiresAt: number } | null;
}

const fresh = (): PageIdentity => ({
  createdSessions: {},
  createdVisitors: {},
  visitor: null,
  session: null,
});

let page: PageIdentity = fresh();

export const IdentityManager = {
  getVisitorId(): string {
    const existing = readCookie(VISITOR_COOKIE) ?? page.visitor;
    const id = existing ?? uuid();
    if (!existing) page.createdVisitors[id] = true;
    page.visitor = id;
    writeCookie(VISITOR_COOKIE, id, daysFromNow(VISITOR_MAX_AGE_DAYS));
    return id;
  },

  /** true si el visitor_id ya existía antes de esta carga de página. */
  isReturningVisitor(): boolean {
    return !page.createdVisitors[IdentityManager.getVisitorId()];
  },

  getSessionId(): string {
    const now = Date.now();
    const remembered =
      page.session && page.session.expiresAt > now ? page.session.id : null;
    const existing = readCookie(SESSION_COOKIE) ?? remembered;
    const id = existing ?? uuid();
    if (!existing) page.createdSessions[id] = true;
    page.session = { id, expiresAt: now + SESSION_MAX_AGE_MS };
    writeCookie(SESSION_COOKIE, id, hoursFromNow(SESSION_MAX_AGE_HOURS));
    return id;
  },

  /**
   * true si `sessionId` lo ha creado esta carga de página: no había cookie (ni
   * valor retenido a la espera del consentimiento) o había caducado. Una
   * pestaña nueva o una recarga de la misma sesión dan false.
   */
  isNewSession(sessionId: string): boolean {
    return !!page.createdSessions[sessionId];
  },

  touchSession(): void {
    IdentityManager.getSessionId();
  },

  getUserId(): string | null {
    return readCookie(USER_COOKIE);
  },

  setUserId(leadId: string): void {
    if (!leadId) return;
    if (readCookie(USER_COOKIE) === leadId) return;
    writeCookie(USER_COOKIE, leadId, daysFromNow(VISITOR_MAX_AGE_DAYS));
  },

  clearUser(): void {
    writeCookie(USER_COOKIE, "", new Date(0));
  },

  /** Solo para tests: olvida lo que sabe esta carga de página. */
  resetForTests(): void {
    page = fresh();
  },
};

export const newClientEventId = (): string => uuid();
